<template>
  <div class="flex flex-col items-center">
    <div
      v-if="!isMainPage"
      class="hidden sm:flex cursor-pointer mb-[16px] lg:max-w-[1056px] md:max-w-[700px] w-full pl-3"
      @click="redirectMainBoard"
    >
      <img
        src="/assets/icons/board/back-to-board.svg"
        class="h-[24px] w-[24px] mr-[4px]"
        alt="back logo"
      />
      <p class="text-[1rem] leading-[1.5rem]">Back</p>
    </div>
    <div
      v-if="isMainPage"
      class="mt-[13px] mb-[13px] lg:max-w-[1056px] md:max-w-[700px] w-full pl-3"
    >
      <div class="flex flex-row items-center cursor-pointer" @click="redirectToSelectYear()">
        <img
          src="/assets/icons/board/back-to-board.svg"
          class="h-[24px] w-[24px] md:h-[32px] md:w-[32px]"
          alt="back logo"
        />
        <p
          class="ml-[6px] font-bold text-[1.5rem] leading-[1.5rem] md:text-[2rem] md:leading-[2rem]"
        >
          {{ currBoard.year }}
        </p>
      </div>
    </div>
    <!-- Mobile: plain flex column avoids waterfall reflow flicker -->
    <div
      v-if="isMobileLayout"
      class="w-full px-[8px]"
      :style="{
        opacity: isLayoutReady ? 1 : 0,
        transition: 'opacity 300ms ease-out',
      }"
    >
      <div v-for="(post, index) in filteredPosts" :key="index" class="bg-[#F8F9F9]">
        <MessageCard
          class="mb-[12px] bg-[#ffffff]"
          :member="post.member"
          :message="post.message"
          :attachment-url-list="post.attachmentUrlList"
          :is-me="post.isMe"
          :post-id="post.id"
          :board-id="post.boardId"
          :is-pinned="post.isPinned"
          :is-hidden="post.isHidden"
          :is-anon="post.isAnon"
          :is-favourite="post.isFavourite"
          :is-read="post.isRead"
          :year-created-at="post.yearOfBoard"
          :tags="post.tags"
          @open-options="updateShowOptionsModal(true)"
        />
      </div>
    </div>
    <!-- Desktop: waterfall layout -->
    <VueFlexWaterfall
      v-else
      ref="boardWaterfall"
      align-content="center"
      col="4"
      col-spacing="8"
      :break-at="{ 1024: 2 }"
      :style="{
        opacity: isLayoutReady ? 1 : 0,
        transition: 'opacity 300ms ease-out',
      }"
    >
      <div v-for="(post, index) in filteredPosts" :key="index" class="bg-[#F8F9F9]">
        <MessageCard
          class="mb-[12px] md:mb-[24px] md:max-w-[328px] md:w-[328px] bg-[#ffffff]"
          :member="post.member"
          :message="post.message"
          :attachment-url-list="post.attachmentUrlList"
          :is-me="post.isMe"
          :post-id="post.id"
          :board-id="post.boardId"
          :is-pinned="post.isPinned"
          :is-hidden="post.isHidden"
          :is-anon="post.isAnon"
          :is-favourite="post.isFavourite"
          :is-read="post.isRead"
          :year-created-at="post.yearOfBoard"
          :tags="post.tags"
          @open-options="updateShowOptionsModal(true)"
          @layout-update="scheduleWaterfallRelayout"
        />
      </div>
      <div v-if="filteredPosts.length < 3" class="flex flex-row">
        <div v-for="(i, index) in range(3 - filteredPosts.length)" :key="index">
          <div
            :id="i"
            class="mb-[12px] max-w-[100vw] md:mb-[24px] md:max-w-[328px] md:w-[328px] bg-transparent"
          />
        </div>
      </div>
    </VueFlexWaterfall>
    <div ref="infiniteLoading" class="w-full" id="infinite-loading">
      <div
        v-if="filteredPosts.length > 0"
        class="h-[40px] mb-[8px] lg:mb-[0px] px-[24px] md:max-w-[700px] lg:max-w-[1056px] w-full mx-auto"
      >
        Hope reading the messages above <br />
        have encouraged you!
      </div>
      <div v-if="filteredPosts.length === 0" class="h-[40px]">
        <EmptyState
          :image="emptyStateContent?.image"
          :title="emptyStateContent.title"
          :body="emptyStateContent.body"
        />
      </div>
    </div>
    <Transition name="fade">
      <MobileModal v-if="showMobileOptionsModal">
        <Transition name="slide-options" appear>
          <MessageOptions
            @close="updateShowOptionsModal(false)"
            @delete-message="showDeletePostToast"
            @pin-message="showPinPostToast"
            @unpin-message="showUnpinPostToast"
            @hide-message="showHidePostToast"
            @unhide-message="showUnhidePostToast"
            @handle-tags-updated="handleTagsUpdated"
          />
        </Transition>
      </MobileModal>
    </Transition>

    <Transition :name="isMobile">
      <MobileModal v-if="showFilterModal">
        <FilterOptions />
      </MobileModal>
    </Transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import InfiniteLoading from 'v3-infinite-loading';
import MobileModal from '@/components/common/MobileModal';
import MessageCard from '@/components/Board/Card/MessageCard.vue';
import MessageOptions from '@/components/Board/Card/MessageOptions';
import FilterOptions from '@/components/Board/Filter/FilterOptions';
import EmptyState from '@/components/Board/EmptyState';
import axios from 'axios';
import { VueFlexWaterfall } from 'vue-flex-waterfall';
import { isNativeNotWeb } from '@/utils/platform-check';

export default {
  components: {
    InfiniteLoading,
    MobileModal,
    MessageCard,
    MessageOptions,
    FilterOptions,
    EmptyState,
    VueFlexWaterfall,
  },
  props: {
    isMyMessages: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFavouriteMessages: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPinnedMessages: {
      type: Boolean,
      required: false,
      default: false,
    },
    isHiddenMessages: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      zone: '',
      posts: [],
      filteredPosts: [],
      page: 1,
      cursor: null,
      perPage: 20,
      moreMessagePerPage: 300,
      taggedPost: false,
      isLoadingPosts: false,
      isLayoutReady: false,
      layoutSettleTimer: null,
      layoutSafetyTimer: null,
      resizeDebounceTimer: null,
      hasStartedInitialReveal: false,
      isMobileLayout: isNativeNotWeb(),
    };
  },
  computed: {
    ...mapState('board', [
      'currBoard',
      'currPost',
      'isCurrBoardAdmin',
      'showOptionsModal',
      'showFilterModal',
      'readFilterValue',
      'myZoneFilterValue',
      'zoneFilters',
      'tagFilters',
    ]),
    ...mapState('user', ['token']),
    ...mapGetters({
      zoneName: 'profile/zoneName',
    }),
    isMobile() {
      return window.innerWidth <= 500 ? 'slide' : 'fade';
    },
    isMainPage() {
      return this.$route.path === '/birthdayboard';
    },
    // showMobileOptionsModal () {
    //   // Check the viewport width and return the appropriate boolean value
    //   return window.innerWidth < 500 && this.showOptionsModal
    // },
    showMobileOptionsModal() {
      // Check the viewport width and return the appropriate boolean value
      return this.showOptionsModal;
    },

    getBoardPostsPath() {
      if (this.isMyMessages) {
        return `/board/posts/my-messages?cursor=${this.cursor}&per_page=${this.perPage}`;
      } else if (this.isFavouriteMessages) {
        return `/board/${this.currBoard.id}/posts/favourite-messages?cursor=${this.cursor}&per_page=${this.perPage}&year=${this.currBoard.year}`;
      } else if (this.isHiddenMessages) {
        return `/board/${this.currBoard.id}/posts/hidden?cursor=${this.cursor}&per_page=${this.perPage}&year=${this.currBoard.year}`;
      } else if (this.isPinnedMessages) {
        return `/board/${this.currBoard.id}/posts/pinned?cursor=${this.cursor}&per_page=${this.perPage}&year=${this.currBoard.year}`;
      } else if (this.isCurrBoardAdmin) {
        return `/board/${this.currBoard.id}/posts/admin?cursor=${this.cursor}&per_page=${this.perPage}&year=${this.currBoard.year}`;
      } else if (
        this.readFilterValue !== 'all' ||
        this.myZoneFilterValue !== 'all' ||
        this.zoneFilters.length > 0
      ) {
        return `/board/${this.currBoard.id}/posts?cursor=${this.cursor}&per_page=${this.moreMessagePerPage}&year=${this.currBoard.year}`;
      } else {
        return `/board/${this.currBoard.id}/posts?cursor=${this.cursor}&per_page=${this.perPage}&year=${this.currBoard.year}`;
      }
    },
    emptyStateContent() {
      if (
        this.$route.path === '/birthdayboard' &&
        this.myZoneFilterValue !== 'myzone' &&
        this.zoneFilters.length === 0
      ) {
        return {
          title: 'Be the first to share your birthday message for SP!',
          body: "Click ‘+’ to add your message! You're moments away from sending some encouragement and appreciation 🥹🎉",
        };
      } else if (this.$route.path === '/birthdayboard' && this.myZoneFilterValue === 'myzone') {
        return {
          title: "Welcome to your zone's very own birthday board for SP!",
          body: "Click ‘+’ and be the first in your zone to share your birthday message for SP! You're moments away from sending some encouragement and appreciation 🥹🎉",
        };
      } else if (this.$route.path === '/birthdayboard' && this.zoneFilters.length > 0) {
        return {
          title: 'View More Messages',
          body: 'Check out the churchwide board or other zone boards for more messages!',
        };
      } else if (this.$route.path === '/birthdayboard/my-messages') {
        return {
          image: 'message-plane',
          title: 'Send Your Birthday Greetings',
          body: "Click ‘+’ to add your message! You're moments away from sending some encouragement and appreciation 🥹🎉",
        };
      } else if (this.$route.path === '/birthdayboard/favourite-messages') {
        return {
          image: 'favourite-message',
          title: 'Save Your Favorite Messages',
        };
      } else if (this.$route.path === '/birthdayboard/pinned-messages') {
        return {
          image: 'pinned-message',
          title: 'No Pinned Messages Yet',
        };
      } else if (this.$route.path === '/birthdayboard/hidden-messages') {
        return {
          image: 'hidden-message',
          title: 'No Hidden Messages Yet',
        };
      } else {
        return {
          title: '',
          body: '',
        };
      }
    },
  },
  watch: {
    currBoard() {
      this.posts = [];
      this.cursor = null;
      this.page = 1;
      this.resetPosts();
      this.loadMorePosts();
      // this.resetPosts();
    },
    readFilterValue() {
      this.resetPosts();
    },
    myZoneFilterValue() {
      this.posts = [];
      this.cursor = null;
      this.resetPosts();
      this.loadMorePosts();
    },
    zoneFilters() {
      this.posts = [];
      this.cursor = null;
      this.resetPosts();
      this.loadMorePosts();
    },
    tagFilters() {
      this.posts = [];
      this.cursor = null;
      this.resetPosts();
      this.loadMorePosts();
    },
    posts() {
      // this.filterPosts();
    },
  },
  async created() {
    await this.$store.dispatch('profile/fetchProfile');
    this.zone = this.zoneName;
  },
  mounted() {
    this.loadMorePosts();
    // Safety timer is NOT started here — loadMorePosts is async and can take
    // seconds. Starting the reveal timer before posts arrive would cause the
    // board to show blank, then flicker when posts + images finally load.
    // The timer is started inside loadMorePosts() after the first response.
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    clearTimeout(this.layoutSettleTimer);
    clearTimeout(this.layoutSafetyTimer);
    clearTimeout(this.resizeDebounceTimer);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = setTimeout(() => {
        this.scheduleWaterfallRelayout();
      }, 150);
    },
    scheduleWaterfallRelayout() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$refs.boardWaterfall?.updateOrder();
        });
      });
      // 250ms gives $nextTick + RAF + updateOrder's internal nextTick time to
      // settle, plus a buffer for any non-preloaded images that arrive shortly
      // after first paint. Keeps the board hidden until layout is truly stable.
      clearTimeout(this.layoutSettleTimer);
      this.layoutSettleTimer = setTimeout(() => {
        this.isLayoutReady = true;
      }, 250);
    },
    refreshPosts() {
      this.posts = [];
      this.filteredPosts = [];
      this.cursor = null;
      this.loadMorePosts();
    },
    resetPosts() {
      // if (this.$refs.infiniteLoading) {
      //   this.$refs.infiniteLoading.stateChanger.reset()
      // }
      // this.posts = [];
      this.filteredPosts = this.posts;
      this.filterPosts();
      // this.page = 1;
      // this.cursor = null;
    },
    async loadMorePosts() {
      // Bail if currBoard isn't populated yet. On cold starts / deep links
      // into /birthdayboard/favourite-messages (and hidden/pinned), this
      // component mounts before BirthdayLayout's fetchBoards finishes, so
      // currBoard is {}. Without this guard we fire a bogus
      // /board/undefined/... request that holds isLoadingPosts=true long
      // enough to block the currBoard watcher's real reload, leaving the
      // list empty. my-messages uses /board/posts/my-messages (no id), so
      // it's exempt.
      if (!this.isMyMessages && !this.currBoard?.id) return;
      if (this.isLoadingPosts) return;
      this.isLoadingPosts = true;
      try {
        const result = await axios.get(process.env.VUE_APP_API_URL + this.getBoardPostsPath, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        const { cursor, posts: additionalItems } = result.data;
        if (additionalItems?.length) {
          const accepted = this.isHiddenMessages
            ? additionalItems
            : additionalItems.filter(post => !post.isHidden);
          // On the very first load, preload thumbnails BEFORE rendering cards
          // so images hit the browser cache. When the cards mount, the <img>
          // tags resolve their src synchronously, the waterfall measures the
          // correct heights on first paint, and the post-render shuffle (the
          // source of the flicker + delay) never happens.
          // Hard-capped so a slow network doesn't stall the initial render.
          if (!this.hasStartedInitialReveal) {
            await this.preloadFirstThumbnails(accepted);
          }
          this.posts.push(...accepted);
          this.filteredPosts = this.posts;
          this.cursor = cursor;
          this.filterPosts();
        }
      } catch (error) {
        console.error('Failed to load posts:', error);
      } finally {
        this.isLoadingPosts = false;
        this.$emit('set-debounce', false);
        this.startInitialReveal();
      }
    },
    preloadFirstThumbnails(posts) {
      // Preload the first 8 thumbnails — covers roughly one and a half screens
      // of above-the-fold cards on most phones. Preloading all 20 saturates a
      // mobile connection. Off-screen late arrivals trigger scheduleWaterfallRelayout.
      const urls = posts
        .slice(0, 8)
        .map(p => p.attachmentUrlList && p.attachmentUrlList[0])
        .filter(Boolean);
      if (urls.length === 0) return Promise.resolve();
      const loads = urls.map(
        url =>
          new Promise(resolve => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = resolve;
            img.src = url;
          }),
      );
      // Race against a 2000ms ceiling — enough headroom for slow connections
      // without stalling the board indefinitely.
      return Promise.race([Promise.all(loads), new Promise(resolve => setTimeout(resolve, 2000))]);
    },
    startInitialReveal() {
      // Only run once, on the first completed load. Subsequent pagination /
      // filter changes don't re-hide the board (would look jarring).
      if (this.hasStartedInitialReveal) return;
      this.hasStartedInitialReveal = true;
      // Seed the settle timer after cards mount. Covers the no-images case
      // where no @load event ever fires — otherwise the board would stay
      // hidden until the 3s safety ceiling below.
      this.$nextTick(() => {
        this.scheduleWaterfallRelayout();
      });
      // Hard ceiling: even if something hangs, reveal after 3s so the user
      // is never stuck on a blank board.
      clearTimeout(this.layoutSafetyTimer);
      this.layoutSafetyTimer = setTimeout(() => {
        this.isLayoutReady = true;
      }, 3000);
    },
    updateShowOptionsModal(showOptionsModal) {
      this.$store.dispatch('board/updateShowOptionsModal', showOptionsModal);
    },
    showDeletePostToast() {
      this.$store.dispatch('board/updateIsBoardToastOpen', true);
      this.$store.dispatch('board/updateBoardToastMessage', 'Post deleted!');
      this.posts = this.posts.filter(post => post.id !== this.currPost.id);
      window.scrollTo(0, 0);
      this.$store.dispatch('board/updateCurrPost', null);
    },
    showPinPostToast() {
      this.$store.dispatch('board/updateIsBoardToastOpen', true);
      this.$store.dispatch('board/updateBoardToastMessage', 'Post pinned!');
      this.posts = this.posts.filter(post => post.id !== this.currPost.id);
      const pinnedPost = Object.assign({}, this.currPost);
      pinnedPost.isPinned = true;
      this.posts.unshift(pinnedPost);
      this.$store.dispatch('board/updateCurrPost', null);
    },
    showUnpinPostToast() {
      this.$store.dispatch('board/updateIsBoardToastOpen', true);
      this.$store.dispatch('board/updateBoardToastMessage', 'Post unpinned!');
      this.posts = this.posts.filter(post => post.id !== this.currPost.id);
      const unpinnedPost = Object.assign({}, this.currPost);
      unpinnedPost.isPinned = false;
      this.insertPost(unpinnedPost);
      this.$store.dispatch('board/updateCurrPost', null);
    },
    showHidePostToast() {
      this.posts = this.posts.map(post => {
        if (post.id === this.currPost.id) {
          post.isHidden = true;
        }
        return post;
      });
      this.$store.dispatch('board/updateIsBoardToastOpen', true);
      this.$store.dispatch('board/updateBoardToastMessage', 'Post hidden!');
      this.$store.dispatch('board/updateCurrPost', null);
    },
    showUnhidePostToast() {
      this.posts = this.posts.map(post => {
        if (post.id === this.currPost.id) {
          post.isHidden = false;
        }
        return post;
      });
      this.$store.dispatch('board/updateIsBoardToastOpen', true);
      this.$store.dispatch('board/updateBoardToastMessage', 'Post unhidden!');
      this.$store.dispatch('board/updateCurrPost', null);
    },
    insertPost(newPost) {
      // insert back in order
      const pinnedPosts = this.posts.filter(post => post.isPinned);
      const unpinnedPosts = this.posts.filter(post => !post.isPinned);
      // find position to insert in unpinned posts
      const index = unpinnedPosts.findIndex(post => post.id > newPost.id);
      if (index === -1) {
        unpinnedPosts.push(newPost);
      } else {
        unpinnedPosts.splice(index, 0, newPost);
      }
      this.posts = [...pinnedPosts, ...unpinnedPosts];
    },
    filterPosts() {
      const pathsBypassClientFilters = [
        '/birthdayboard/favourite-messages',
        '/birthdayboard/pinned-messages',
        '/birthdayboard/hidden-messages',
        '/birthdayboard/my-messages',
      ];
      if (pathsBypassClientFilters.includes(this.$route.path)) {
        this.filteredPosts = [...this.posts];
        this.scheduleWaterfallRelayout();
        return;
      }

      // Filter posts based on readFilterValue first
      let intermediatePosts = [];
      if (this.readFilterValue === 'all' || this.$route.path !== '/birthdayboard') {
        intermediatePosts = [...this.posts];
      } else if (this.readFilterValue === 'read') {
        intermediatePosts = this.filteredPosts.filter(post => post.isRead === true);
      } else if (this.readFilterValue === 'unread') {
        intermediatePosts = this.filteredPosts.filter(post => post.isRead === false);
      }

      let intermediatePosts2 = [];
      // Filter on MyZoneFilterValue
      if (this.myZoneFilterValue === 'myzone') {
        intermediatePosts2 = intermediatePosts.filter(post => {
          if (!post.isAnon) {
            return post.member.zone.toUpperCase() === this.zone.toUpperCase();
          } else {
            return '';
          }
        });
      } else if (this.zoneFilters.length > 0 && this.myZoneFilterValue !== 'myzone') {
        intermediatePosts2 = intermediatePosts.filter(post => {
          if (!post.isAnon) {
            return this.zoneFilters.includes(post.member.zone.toUpperCase());
          } else {
            return '';
          }
        });
      } else {
        intermediatePosts2 = [...intermediatePosts];
      }

      let intermediatePosts3 = [];
      // Filter on TagFilterValue
      if (this.isCurrBoardAdmin && this.tagFilters.length > 0) {
        intermediatePosts3 = intermediatePosts2.filter(post => {
          return this.tagFilters.some(tag => post.tags.includes(tag));
        });
      } else {
        intermediatePosts3 = [...intermediatePosts2];
      }
      this.filteredPosts = intermediatePosts3;
      this.scheduleWaterfallRelayout();
    },
    range(count) {
      return Array(count)
        .fill()
        .map((_, index) => index);
    },
    redirectMainBoard() {
      this.$router.push('/birthdayboard');
    },
    redirectToSelectYear() {
      this.$store.dispatch('board/updateIsYearSelected', false);
      this.$store.dispatch('board/resetFilters');
    },
    handleTagsUpdated({ postId, tags }) {
      if (!postId) return;
      // Find the post and update its tags
      const postIndex = this.posts.findIndex(post => post.id === postId);
      if (postIndex !== -1) {
        this.posts[postIndex].tags = tags;
      }
      // Optionally, re-filter posts if necessary
      this.filterPosts();
    },
  },
};
</script>

<style scoped>
.slide-enter,
.slide-leave-to {
  transform: translateY(100%);
}
.slide-enter-active,
.slide-leave-active {
  transition: 0.4s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: 0.2s ease-in-out;
}

.slide-options-enter,
.slide-options-leave-to {
  transform: translateY(100%);
}

.slide-options-enter-active,
.slide-options-leave-active {
  transition: all 0.4s ease-in 0.15s;
}
</style>
